import {
  isToday,
  isYesterday,
  formatDateTime,
  convertTrackingEventDateTime,
} from '@pperform/pp-utils/datetime.utils';
import { HIGHLIGHT_EVENTS_TYPE } from '@pperform/pp-constants/widgetConfigs';
import { MixedEvent, ParcelEvent } from '@pperform/pp-data-mappers';
import type { TFunction } from 'i18next';

export const getHighlightStatus = (
  isLatestEvent: boolean,
  highlightEventsType: HIGHLIGHT_EVENTS_TYPE,
): boolean => {
  if (highlightEventsType === HIGHLIGHT_EVENTS_TYPE.ALL) {
    return true;
  }

  if (highlightEventsType === HIGHLIGHT_EVENTS_TYPE.ONLY_LATEST) {
    return isLatestEvent;
  }

  return false;
};

type Event = MixedEvent | ParcelEvent;

const getEventTimeSection = (
  event: Event,
  {
    timezone,
    timeformat,
    locale,
    translator,
  }: {
    locale: string;
    timezone: string;
    timeformat: string;
    translator: TFunction;
  },
): string => {
  const { eventTime, parsedEventTimeTimezone } = event;
  const { convertedTime } = convertTrackingEventDateTime(
    eventTime,
    parsedEventTimeTimezone,
    timezone,
  );

  if (isToday(convertedTime)) {
    return translator('today');
  }

  if (isYesterday(convertedTime)) {
    return translator('yesterday');
  }

  return formatDateTime(convertedTime, timeformat, locale);
};

export const groupEventsByDate = (
  events: Array<Event>,
  {
    timezone,
    timeformat,
    locale,
    translator,
  }: {
    locale: string;
    timezone: string;
    timeformat: string;
    translator: TFunction;
  },
): [string, Event[]][] => {
  const map = events.reduce((acc, event) => {
    const section = getEventTimeSection(event, {
      timezone,
      timeformat,
      locale,
      translator,
    });

    if (!acc.get(section)) {
      acc.set(section, []);
    }
    acc.get(section).push(event);

    return acc;
  }, new Map<string, Event[]>());

  return Array.from(map.entries());
};

export const uppercaseFirstLetter = (text: string): string => {
  if (!text) {
    return text;
  }

  return text.charAt(0).toUpperCase() + text.slice(1);
};
